import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { AppShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SpeakInsteadButton } from "@/pages/Incidents";
import { VoiceTextarea } from "@/lib/speech";
import { Sparkles, Save, Download, Trash2, Share2, AlertTriangle, Upload, X, Check, FileText, ChevronDown, ChevronUp, Info, Calendar, MapPin, Users, ScrollText, Clock, Bot, BadgeCheck, Camera, Mic, Paperclip, FileCheck2, Award, ShieldCheck, ClipboardList, Gavel, ExternalLink } from "lucide-react";

/** Popup-blocker-safe PDF delivery: anchor + download attribute works on
 *  desktop and mobile without depending on window.open. */
export const savePdfBlob = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

/** Blank test — mirrors backend _blank(). */
const _blank = (v) => {
  if (v === undefined || v === null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
};

/** A draft is considered COMPLETELY empty only when ALL of these are true.
 *  Mirrors backend _case_is_empty() byte-for-byte so client + server agree. */
export const caseIsEmpty = (c) => {
  if (!c) return true;
  return (
    _blank(c.what_happened) &&
    (_blank(c.primary_category) && _blank(c.secondary_categories)) &&
    _blank(c.incident_date) &&
    (_blank(c.work_location) && _blank(c.where_happened)) &&
    (_blank(c.who_involved) && _blank(c.witnesses)) &&
    (_blank(c.supporting_facts) && _blank(c.mgmt_said) && _blank(c.employee_said) && _blank(c.instructions_given) && _blank(c.employee_complied) && _blank(c.prior_incidents) && _blank(c.records_evidence)) &&
    (_blank(c.selected_articles) && _blank(c.contract_provisions)) &&
    _blank(c.evidence) &&
    _blank(c.grievance_statement) &&
    (_blank(c.selected_remedies) && _blank(c.remedy_statement))
  );
};

/** Derive the wizard step to resume at from saved case data alone.
 *  Returns the first incomplete step index (0-5); Review (5) if all done. */
const resumeStepFor = (d) => {
  const filled = (v) => !!(v && String(v).trim());
  const laterData = (d.selected_articles || []).length > 0 || filled(d.grievance_statement) ||
    (d.selected_remedies || []).length > 0 || filled(d.remedy_statement);
  const done = [
    filled(d.what_happened) && d.what_happened.trim().length >= 40 && filled(d.primary_category),
    filled(d.incident_date),
    filled(d.work_location) || filled(d.where_happened),
    filled(d.who_involved) || filled(d.witnesses) || laterData,
    (d.selected_articles || []).length > 0 && filled(d.grievance_statement) &&
      ((d.selected_remedies || []).length > 0 || filled(d.remedy_statement)),
  ];
  const idx = done.findIndex(x => !x);
  return idx === -1 ? 5 : idx;
};

const STEPS = [
  { key:"what", label:"What Happened", short:"What", icon: ScrollText },
  { key:"when", label:"When", short:"When", icon: Calendar },
  { key:"where", label:"Where", short:"Where", icon: MapPin },
  { key:"who", label:"Who Was Involved", short:"Who", icon: Users },
  { key:"analysis", label:"Article Analysis", short:"Analysis", icon: Sparkles },
  { key:"review", label:"Review", short:"Review", icon: Check },
];

const EVIDENCE_TYPES = ["Warning letter","Disciplinary notice","Pay stub","Timecard","Schedule","Text messages","Emails","Photographs","Video","Medical document","DOT document","Attendance record","Seniority list","Job posting","Dispatch record","Telematics record","Witness statement","Other"];

const REMEDIES = ["Cease and desist","Remove discipline from all records","Make the employee whole","Restore lost wages","Restore benefits","Restore seniority","Pay all contractual penalties","Award the missed overtime opportunity","Correct payroll records","Return the employee to the proper position","Reinstate the employee","Reimburse expenses","Stop supervisors from performing bargaining-unit work","Provide a safe working environment","Comply with the collective bargaining agreement","Any other remedy deemed appropriate"];

const Field = ({label, hint, children, testid, req}) => (
  <label className="block" data-testid={testid}>
    <span className="block text-sm font-medium text-slate-700 mb-1">{label}{req && <span className="text-red-600"> *</span>}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
  </label>
);
export const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 focus:border-red-500 focus:outline-none";

/** Collapsible "more detail" section — progressive disclosure. */
function MoreDetail({ title, children, testid }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-dashed border-slate-300 rounded-md" data-testid={testid}>
      <button type="button" onClick={()=>setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
        <span className="flex items-center gap-2"><Info className="h-4 w-4 text-slate-400"/>{title}</span>
        {open ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}
      </button>
      {open && <div className="p-3 border-t border-slate-200 space-y-3">{children}</div>}
    </div>
  );
}

const confidenceFor = (c) => {
  if (!c) return null;
  const cat = c.primary_category || "";
  const articles = c.selected_articles || [];
  const hasFacts = !!(c.what_happened && c.what_happened.length > 40);
  if (cat && articles.length > 0 && hasFacts) return "High";
  if (cat && articles.length > 0) return "Medium";
  if (cat || articles.length > 0) return "Low";
  return null;
};

export const PdfUpgradeDialog = ({ open, onOpenChange, nav }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="pdf-upgrade-dialog" className="max-w-md">
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-red-600" /> Ready for your next grievance?
        </AlertDialogTitle>
        <AlertDialogDescription className="space-y-2 text-left">
          <span className="block">Your first completed grievance PDF was free — and it stays free to re-download anytime.</span>
          <span className="block">Premium unlocks completed grievance PDFs for additional grievances, so every case you document can become a filing-ready document.</span>
          <span className="block text-slate-500 text-xs">Your drafts, incidents, evidence, and Workplace Timeline are always saved — upgrading never changes your data.</span>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="pdf-upgrade-not-now">Not Now</AlertDialogCancel>
        <AlertDialogAction data-testid="pdf-upgrade-upgrade-btn" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => nav("/billing")}>
          Upgrade to Premium
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export function GrievanceBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [caseId, setCaseId] = React.useState(id);
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const [c, setC] = React.useState({});
  const [categories, setCategories] = React.useState([]);
  const [suggestions, setSuggestions] = React.useState([]);
  const [warning, setWarning] = React.useState("");
  const [specialWarnings, setSpecialWarnings] = React.useState([]);
  const [libraryArticles, setLibraryArticles] = React.useState([]);
  const [libraryQ, setLibraryQ] = React.useState("");
  const [genBusy, setGenBusy] = React.useState(false);
  const [deadlineHint, setDeadlineHint] = React.useState(null);
  const [supplementHint, setSupplementHint] = React.useState(null);
  const [supplementOverride, setSupplementOverride] = React.useState("");
  const [showAgreementDropdown, setShowAgreementDropdown] = React.useState(false);
  const [aiCat, setAiCat] = React.useState(null); // {primary_category, confidence, related_issues, message}
  const [aiCatBusy, setAiCatBusy] = React.useState(false);
  const [aiCatDismissed, setAiCatDismissed] = React.useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = React.useState(true);
  const aiTimer = React.useRef(null);
  const { user } = useAuth();
  const saveTimer = React.useRef(null);
  /** Pending accumulated patch — merged with every update() call and flushed
   *  as a single PUT when the debounce fires. Prevents rapid successive
   *  updates from cancelling each other's persistence. */
  const pendingPatchRef = React.useRef({});
  const inflightSaveRef = React.useRef(false);
  const cRef = React.useRef(c);           // always-latest snapshot for unmount/unload
  React.useEffect(() => { cRef.current = c; }, [c]);
  const caseIdRef = React.useRef(caseId);
  React.useEffect(() => { caseIdRef.current = caseId; }, [caseId]);
  const [pendingExit, setPendingExit] = React.useState(null);  // "/dashboard" | null
  const [newReady, setNewReady] = React.useState(false);       // blank wizard ready (no DB case yet)
  const [existingDraft, setExistingDraft] = React.useState(null);
  const suppressCleanupRef = React.useRef(false);   // set true after explicit Discard

  /** Attempt to leave the wizard. If the case is completely empty, prompt
   *  for confirmation before deleting; otherwise navigate immediately. */
  const attemptExit = React.useCallback((target = "/dashboard") => {
    if (caseIsEmpty(cRef.current)) {
      setPendingExit(target);
    } else {
      nav(target);
    }
  }, [nav]);

  /** Fire-and-forget delete-if-empty; safe on unmount / browser close.
   *  Uses fetch({keepalive:true}) because sendBeacon can't attach the
   *  Authorization header. The endpoint is idempotent and refuses to delete
   *  anything but a truly empty, owner-matched Draft. */
  const cleanupIfEmpty = React.useCallback(() => {
    if (suppressCleanupRef.current) return;
    if (!caseId) return;
    if (!caseIsEmpty(cRef.current)) return;
    try {
      const token = localStorage.getItem("fmg_token") || "";
      fetch(`${api.defaults.baseURL}/cases/${caseId}/delete-if-empty`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: "{}",
        keepalive: true,
      });
    } catch {}
  }, [caseId]);

  React.useEffect(() => {
    if (!caseId) return;
    const onLeave = () => cleanupIfEmpty();
    window.addEventListener("beforeunload", onLeave);
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("beforeunload", onLeave);
      window.removeEventListener("pagehide", onLeave);
      // Also fire on SPA unmount (sidebar link click, logo click, etc.).
      // Endpoint is idempotent so double-calls are harmless.
      cleanupIfEmpty();
    };
  }, [caseId, cleanupIfEmpty]);

  const confirmDiscard = async () => {
    suppressCleanupRef.current = true;   // avoid a duplicate call from unmount
    if (caseId) { try { await api.post(`/cases/${caseId}/delete-if-empty`); } catch {} }
    const target = pendingExit || "/dashboard";
    setPendingExit(null);
    nav(target);
  };

  React.useEffect(()=>{ api.get("/categories").then(r=>setCategories(r.data)); }, []);

  React.useEffect(()=>{
    if (id) api.get(`/cases/${id}`).then(r=>{
      setC(r.data);
      setSupplementOverride(r.data.detected_supplement || "");
      setStep(resumeStepFor(r.data));
    }).catch(()=>toast.error("Case not found"));
    else {
      setStep(0);
      // Navigation must never create a case. Render a blank local wizard;
      // the DB case is created on the member's first real input (see
      // flushPendingPatch). Offer any existing unfinished draft instead.
      setC({});
      setCaseId(null);
      caseIdRef.current = null;
      setNewReady(true);
      api.get("/cases").then(r => {
        const draft = (r.data || []).find(x => x.status === "Draft" && !x.archived);
        if (draft) setExistingDraft(draft);
      }).catch(() => {});
    }
  }, [id]);

  const update = (patch) => {
    setC(prev => ({...prev, ...patch}));
    // Accumulate pending patch so rapid successive update() calls never
    // silently drop fields via debounce cancellation.
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushPendingPatch, 900);
  };

  /** Persist the accumulated pending patch. Updates arriving while the
   *  request is in flight remain in pendingPatchRef and are saved on
   *  the next tick. Failed saves rewind the snapshot back onto pending
   *  (retried automatically on the next update() or debounce). */
  const flushPendingPatch = React.useCallback(async () => {
    if (inflightSaveRef.current) {
      // A save is already in flight — reschedule to run soon after it settles.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(flushPendingPatch, 300);
      return;
    }
    if (Object.keys(pendingPatchRef.current).length === 0) return;
    inflightSaveRef.current = true;
    try {
      let cid = caseIdRef.current;
      if (!cid) {
        // Lazy creation: the case is created by the member's first real
        // input, never by navigation.
        try {
          const r = await api.post("/cases", { title: "" });
          cid = r.data.id;
          caseIdRef.current = cid;
          setCaseId(cid);
          setC(prev => ({ ...r.data, ...prev, id: cid }));
          window.history.replaceState({}, "", `/new-grievance/${cid}`);
        } catch (e) {
          if (e.response?.status === 402) { toast.error(e.response?.data?.detail || "Upgrade required"); nav("/billing"); }
          return; // pending patch retained; retried on next update/debounce
        }
      }
      const snapshot = pendingPatchRef.current;
      pendingPatchRef.current = {};
      try {
        await api.put(`/cases/${cid}`, snapshot);
      } catch {
        // Merge the failed snapshot back onto pending WITHOUT overwriting any
        // newer keys that arrived while we were awaiting the network.
        pendingPatchRef.current = { ...snapshot, ...pendingPatchRef.current };
      }
    } finally {
      inflightSaveRef.current = false;
      if (Object.keys(pendingPatchRef.current).length > 0) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(flushPendingPatch, 300);
      }
    }
  }, [nav]);

  const fetchDeadline = async (cat) => {
    if (!cat) return setDeadlineHint(null);
    const { data } = await api.get(`/hints/deadline?category=${encodeURIComponent(cat)}`);
    setDeadlineHint(data);
  };
  const fetchSupplement = async () => {
    try {
      // Smart detector: uses full profile + case work_location as override.
      const { data } = await api.post("/agreement/detect", {
        work_location: c.work_location || undefined,
      });
      setSupplementHint(data);
      // Auto-fill only if we don't already have a case override
      if (data.supplement && !supplementOverride) {
        setSupplementOverride(data.supplement);
        update({ detected_supplement: data.supplement });
      }
    } catch (e) { /* silent — user can still pick manually */ }
  };

  const confirmAgreement = async (supp) => {
    setSupplementOverride(supp);
    update({ detected_supplement: supp });
    setShowAgreementDropdown(false);
    try {
      await api.post("/agreement/confirm", { supplement: supp });
      toast.success("Agreement saved to your profile. Future grievances will pre-fill this.");
    } catch { /* non-blocking */ }
  };

  React.useEffect(()=>{ if (c.primary_category) fetchDeadline(c.primary_category); }, [c.primary_category]);
  React.useEffect(()=>{ if (step === 2 && !supplementHint) fetchSupplement(); }, [step]);

  /** Ask once, remember, carry it forward: when the primary_category
   *  reliably implies a fact FMG would otherwise ask, apply the inference
   *  and surface it as an editable conclusion (not a silent hide).
   *  Only fires when the underlying field is currently unset (null/undefined)
   *  so a member's explicit answer is never overwritten. */
  const [inferenceApplied, setInferenceApplied] = React.useState({});
  React.useEffect(() => {
    const cat = (c.primary_category || "").toLowerCase();
    if (!cat) return;
    const patch = {}; const applied = {};
    const impliesDiscipline = cat.includes("discharge") || cat.includes("termination")
      || cat.includes("suspension") || cat.includes("warning") || cat.includes("discipline");
    const impliesRemoved = cat.includes("discharge") || cat.includes("termination");
    const impliesNotRemoved = cat.includes("warning");
    if (impliesDiscipline && (c.discipline_issued === undefined || c.discipline_issued === null)) {
      patch.discipline_issued = true;
      applied.discipline_issued = { value: true, why: `Category "${c.primary_category}" indicates discipline was issued.` };
    }
    if (impliesRemoved && (c.removed_from_service === undefined || c.removed_from_service === null)) {
      patch.removed_from_service = true;
      applied.removed_from_service = { value: true, why: `Discharge/termination cases involve removal from service.` };
    } else if (impliesNotRemoved && (c.removed_from_service === undefined || c.removed_from_service === null)) {
      patch.removed_from_service = false;
      applied.removed_from_service = { value: false, why: `Warning letters do not typically remove a member from service.` };
    }
    if (Object.keys(patch).length > 0) {
      update(patch);
      setInferenceApplied(prev => ({ ...prev, ...applied }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.primary_category]);

  // Debounced AI categorization based on 'what_happened' narrative
  React.useEffect(() => {
    if (aiTimer.current) clearTimeout(aiTimer.current);
    const text = (c.what_happened || "").trim();
    if (text.length < 20 || aiCatDismissed) { setAiCat(null); return; }
    aiTimer.current = setTimeout(async () => {
      setAiCatBusy(true);
      try {
        const { data } = await api.post("/ai/categorize", { text });
        setAiCat(data);
      } catch { setAiCat(null); }
      finally { setAiCatBusy(false); }
    }, 2200);
    return () => { if (aiTimer.current) clearTimeout(aiTimer.current); };
  }, [c.what_happened, aiCatDismissed]);

  const useAiRecommendation = () => {
    if (aiCat?.primary_category) {
      update({ primary_category: aiCat.primary_category });
      setShowCategoryDropdown(false);
      toast.success(`Category set to "${aiCat.primary_category}"`);
    }
  };
  const chooseDifferentCategory = () => {
    setAiCatDismissed(true);
    setShowCategoryDropdown(true);
    setTimeout(() => {
      const el = document.querySelector("[data-testid='primary-category']");
      if (el) el.focus();
    }, 50);
  };

  const suggest = async () => {
    const { data } = await api.post("/articles/suggest", { primary_category: c.primary_category, secondary_categories: c.secondary_categories||[] });
    setSuggestions(data.suggestions); setWarning(data.warning); setSpecialWarnings(data.special_warnings||[]);
    return data;
  };

  const searchLib = async () => {
    const { data } = await api.get(`/articles${libraryQ?`?q=${encodeURIComponent(libraryQ)}`:''}`);
    setLibraryArticles(data);
  };

  const toggleArticle = (a) => {
    const cur = c.selected_articles || [];
    const exists = cur.find(x=>x.article_number===a.article_number);
    const next = exists ? cur.filter(x=>x.article_number!==a.article_number) : [...cur, {article_number:a.article_number, article_title:a.article_title, article_summary:a.article_summary}];
    update({ selected_articles: next });
  };
  const isSelected = (n) => (c.selected_articles||[]).some(x=>x.article_number===n);

  const generate = async () => {
    if (!caseIdRef.current) { toast.info("Describe your grievance first — it will save automatically."); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/generate`);
      setC(prev=>({...prev, ...data}));
      toast.success("Grievance language generated");
      return data;
    } catch { toast.error("Generation failed"); throw new Error("gen_failed"); }
    finally { setGenBusy(false); }
  };

  const suggestRemedy = async () => {
    if (!caseIdRef.current) { toast.info("Describe your grievance first — it will save automatically."); return; }
    const { data } = await api.post(`/cases/${caseId}/remedy`);
    update({ selected_remedies: data.suggested_remedies, remedy_statement: data.remedy_statement });
    toast.success("Remedy suggested");
    return data;
  };

  /** Analyze My Case — one-tap orchestration of the three existing endpoints:
   *  articles/suggest → cases/{id}/generate → cases/{id}/remedy.
   *  Guarded to prevent duplicate calls and re-analysis loops. */
  const [analyzeBusy, setAnalyzeBusy] = React.useState(false);
  const [analyzeStage, setAnalyzeStage] = React.useState(""); // articles | drafting | remedy | done
  const [analyzeError, setAnalyzeError] = React.useState("");
  const analyzeAutoDoneRef = React.useRef(false);

  /** Genuinely-required fields to draft an intelligible grievance. Supervisor
   *  is intentionally NOT required — a member may not know or have one for
   *  the specific grievance. */
  const analysisGateReasons = React.useMemo(() => {
    const missing = [];
    if (!(c.what_happened && c.what_happened.trim().length >= 40))
      missing.push("A description of what happened (at least a sentence or two)");
    if (!c.primary_category) missing.push("A primary category");
    if (!c.incident_date) missing.push("The date of the incident");
    if (!((c.work_location && c.work_location.trim()) || (c.where_happened && c.where_happened.trim())))
      missing.push("A work location or where it happened");
    return missing;
  }, [c.what_happened, c.primary_category, c.incident_date, c.work_location, c.where_happened]);
  const analysisGateOk = analysisGateReasons.length === 0;

  /** Has analysis already produced content on the draft? */
  const analysisAlreadyDone = !!((c.grievance_statement && c.grievance_statement.trim())
    || (c.supporting_facts && c.supporting_facts.trim())
    || ((c.selected_remedies || []).length > 0 && (c.remedy_statement || "").trim()));

  const analyzeCase = async ({ forceRegen = false } = {}) => {
    if (analyzeBusy) return;
    if (!analysisGateOk) return;
    setAnalyzeError("");
    setAnalyzeBusy(true);
    try {
      setAnalyzeStage("articles");
      const suggestResult = await suggest();
      // The rules-based /articles/suggest endpoint returns matches ordered by
      // relevance. The FIRST entry is the one the UI labels "Primary" — we
      // treat that single suggestion as FMG's Primary recommendation and
      // auto-add it to selected_articles so generate() has a real provision
      // to cite. Additional suggestions remain opt-in for the member.
      const suggestions = (suggestResult && suggestResult.suggestions) || [];
      const primary = suggestions[0] || null;
      const priorArticles = cRef.current.selected_articles || [];
      const primaryAlreadyOn = primary && priorArticles.some(x => x.article_number === primary.article_number);
      if (primary && !primaryAlreadyOn) {
        const nextArticles = [
          ...priorArticles,
          {
            article_number: primary.article_number,
            article_title: primary.article_title,
            article_summary: primary.article_summary,
            auto_added_by_fmg: true,
          },
        ];
        // Persist synchronously so backend generate() sees the updated list.
        await api.put(`/cases/${caseIdRef.current}`, { selected_articles: nextArticles });
        setC(prev => ({ ...prev, selected_articles: nextArticles }));
        toast.info(`FMG selected ${primary.article_number} as the primary provision. You can remove or change it below.`);
      } else if (!primary) {
        toast.info("Contract provision selection needs review — analysis did not return a supported primary article.");
      }
      setAnalyzeStage("drafting");
      await generate();  // populates grievance_statement / supporting_facts / contract_provisions
      // Only overwrite remedy on explicit regeneration or when none exists.
      if (forceRegen || !((cRef.current.selected_remedies || []).length > 0 && (cRef.current.remedy_statement || "").trim())) {
        setAnalyzeStage("remedy");
        await suggestRemedy();
      }
      setAnalyzeStage("done");
      analyzeAutoDoneRef.current = true;
      toast.success("Analysis complete — review and edit below");
    } catch (e) {
      setAnalyzeError(e?.message === "gen_failed"
        ? "AI generation failed. You can edit the draft manually below or Regenerate."
        : "Analysis failed. You can edit manually or Regenerate.");
      analyzeAutoDoneRef.current = true; // stop auto-retry loop
    } finally {
      setAnalyzeBusy(false);
    }
  };

  /** Auto-run once when the member first lands on Step 5, if gating passes and
   *  no prior analysis has been produced. Prevents duplicate calls via a ref
   *  guard that survives step revisits. */
  React.useEffect(() => {
    if (step !== 4) return;
    if (analyzeBusy) return;
    if (analyzeAutoDoneRef.current) return;
    if (!analysisGateOk) return;
    if (analysisAlreadyDone) { analyzeAutoDoneRef.current = true; return; }
    analyzeCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, analysisGateOk, analysisAlreadyDone]);

  const toggleRemedy = (r) => {
    const cur = c.selected_remedies || [];
    update({ selected_remedies: cur.includes(r) ? cur.filter(x=>x!==r) : [...cur, r] });
  };

  const setStatus = async (status) => {
    if (!caseIdRef.current) { toast.info("Describe your grievance first — it will save automatically."); return; }
    if (status === "Filed") {
      if (!window.confirm("Confirm that this grievance was actually FILED with the Company. Only click OK if you (or your steward) delivered/submitted it. Otherwise use 'Ready for Steward' or 'Provided to Steward'.")) return;
    }
    try {
      await api.post(`/cases/${caseId}/status`, { status });
      setC(prev=>({...prev, status}));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.info(e.response?.data?.detail || "This status requires Premium. Taking you to Billing to see your options.");
        nav("/billing");
      } else {
        toast.info(e.response?.data?.detail || "Status update failed");
      }
    }
  };

  const [pdfBusy, setPdfBusy] = React.useState(false);
  const downloadPdf = async () => {
    if (!caseIdRef.current) { toast.info("Describe your grievance first — it will save automatically."); return; }
    if (pdfBusy) return;
    if (!c.pdf_generated_at && !readiness.isPrepared) {
      toast.info(`Your grievance isn't ready for a PDF yet. Still needed: ${readiness.missing.map(m => m.label).join("; ")}.`);
      return;
    }
    setPdfBusy(true);
    try {
      const r = await api.get(`/cases/${caseId}/pdf`, { responseType: "blob" });
      savePdfBlob(r.data, `${c.case_number || "FMG-Grievance"}.pdf`);
      toast.success("Grievance PDF downloaded.");
    } catch (e) {
      if (e.response?.status === 402) setShowUpgrade(true);
      else toast.error(e.response?.status === 400 && e.response?.data?.detail ? e.response.data.detail : "PDF generation failed. Please try again.");
    } finally { setPdfBusy(false); }
  };

  const uploadEvidence = async (file, type, description) => {
    if (!caseIdRef.current) { toast.info("Describe your grievance first — it will save automatically."); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("evidence_type", type||"Other"); fd.append("description", description||"");
    try {
      const { data } = await api.post(`/cases/${caseId}/evidence/upload`, fd, { headers:{"Content-Type":"multipart/form-data"}});
      const evidence = [...(c.evidence||[]), {file_id:data.file_id, filename:data.filename, evidence_type:type, description}];
      setC(prev=>({...prev, evidence}));
      toast.success("Evidence uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const confidence = confidenceFor(c);

  /** Grievance-readiness: distinct from _case_is_empty. A member may save an
   *  incomplete draft indefinitely, but the "Mark as Prepared" affirmation
   *  should only be available when the grievance contains the fields that
   *  make it an actually preparable document. */
  const readiness = React.useMemo(() => {
    const missing = [];
    if (!(c.what_happened && c.what_happened.trim().length >= 40))
      missing.push({ key: "what_happened", label: "Description of what happened", step: 0 });
    if (!c.primary_category)
      missing.push({ key: "primary_category", label: "Primary grievance category", step: 0 });
    if (!c.incident_date)
      missing.push({ key: "incident_date", label: "Date of the incident", step: 1 });
    if (!((c.work_location && c.work_location.trim()) || (c.where_happened && c.where_happened.trim())))
      missing.push({ key: "location", label: "Work location or where it happened", step: 2 });
    if (!((c.selected_articles || []).length > 0))
      missing.push({ key: "articles", label: "At least one applicable contract article", step: 4 });
    if (!(c.grievance_statement && c.grievance_statement.trim()))
      missing.push({ key: "statement", label: "A drafted grievance statement", step: 4 });
    const hasRemedy = ((c.selected_remedies || []).length > 0) || (c.remedy_statement && c.remedy_statement.trim());
    if (!hasRemedy)
      missing.push({ key: "remedy", label: "A requested remedy", step: 4 });
    return { missing, isPrepared: missing.length === 0 };
  }, [c.what_happened, c.primary_category, c.incident_date, c.work_location, c.where_happened,
      c.selected_articles, c.grievance_statement, c.selected_remedies, c.remedy_statement]);

  if (!caseId && !newReady) return <AppShell><div>Loading...</div></AppShell>;

  return (
    <AppShell>
      <PdfUpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} nav={nav} />
      <AlertDialog open={!!existingDraft} onOpenChange={(o)=>{ if (!o) setExistingDraft(null); }}>
        <AlertDialogContent data-testid="unfinished-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have an unfinished draft</AlertDialogTitle>
            <AlertDialogDescription>
              {existingDraft?.case_number} — {existingDraft?.title || "Untitled grievance"}. Continue that draft, or start a new grievance?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unfinished-continue-btn" onClick={()=>{ const d = existingDraft; setExistingDraft(null); if (d) nav(`/new-grievance/${d.id}`); }}>Continue Draft</AlertDialogCancel>
            <AlertDialogAction data-testid="unfinished-start-new-btn" onClick={()=>setExistingDraft(null)} className="bg-red-600 hover:bg-red-700 text-white">Start New Grievance</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-slate-500 font-mono">{c.case_number}</div>
          <h1 className="text-2xl font-black font-display text-slate-900">New Grievance</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="builder-pdf"><Download className="h-4 w-4 mr-1"/>{pdfBusy ? "Generating..." : "PDF"}</Button>
          <Button variant="outline" size="sm" onClick={()=>attemptExit("/dashboard")} data-testid="builder-close">Back to Dashboard</Button>
        </div>
      </div>

      {/* Stepper */}
      <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="stepper">
        {STEPS.map((s,i)=>{
          const state = step===i?"step-active":step>i?"step-done":"step-pending";
          const Icon = s.icon;
          return (
            <button key={s.key} onClick={()=>setStep(i)} data-testid={`step-btn-${i}`}
              className={`transition-lift px-2 py-2 rounded-md text-xs font-semibold flex items-center gap-1 justify-center ${state}`}>
              <Icon className="h-3.5 w-3.5"/><span className="hidden sm:inline">{s.label}</span><span className="sm:hidden">{i+1}. {s.short}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-6 md:p-8">
        {/* STEP 1: What Happened */}
        {step===0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-display">Step 1 — What Happened</h2>
            <p className="text-sm text-slate-600">Describe the facts in the order they happened. Include names, dates, times, locations, instructions, and statements. Avoid insults, assumptions, or unrelated information.</p>
            <Field label="Case title" req><input data-testid="case-title" className={inputCls} value={c.title||""} onChange={e=>update({title:e.target.value})} placeholder="e.g., Supervisor working — package car"/></Field>
            <Field label="What happened?" req>
              <VoiceTextarea
                testid="what-speak"
                rows={6}
                className={inputCls}
                value={c.what_happened||""}
                onChange={v=>update({what_happened:v})}
                placeholder="Tell the story in plain language..."
                ariaLabel="What happened"
              />
            </Field>

            {(aiCatBusy || aiCat) && !aiCatDismissed && (
              <div className="rounded-md border-2 border-blue-100 bg-blue-50/60 p-5" data-testid="ai-recommendation-card">
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-blue-700"/>
                  <div className="font-bold font-display text-slate-900">AI Recommendation</div>
                  {aiCatBusy && <span className="text-xs text-slate-500 animate-pulse">Analyzing...</span>}
                </div>
                {!aiCatBusy && aiCat && (aiCat.primary_category && aiCat.confidence >= 0.7 ? (
                  <div className="mt-3 space-y-3">
                    <p className="text-sm text-slate-700">Based on your description, we believe this incident is most closely related to:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Primary Category</div>
                        <div className="mt-1 font-bold text-slate-900" data-testid="ai-rec-category">{aiCat.primary_category}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Confidence</div>
                        <div className="mt-1 font-bold text-slate-900" data-testid="ai-rec-confidence">{Math.round(aiCat.confidence * 100)}%</div>
                        <div className="mt-1 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-blue-600" style={{width:`${Math.round(aiCat.confidence*100)}%`}}/></div>
                      </div>
                    </div>
                    {aiCat.related_issues?.length > 0 && (
                      <div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Related Issues Detected</div>
                        <ul className="mt-1 flex flex-wrap gap-1.5" data-testid="ai-rec-related">
                          {aiCat.related_issues.map((r,i)=><li key={i} className="text-xs bg-white border border-blue-200 rounded-full px-2.5 py-1 text-slate-700">{r}</li>)}
                        </ul>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" onClick={useAiRecommendation} className="bg-red-600 hover:bg-red-700 text-white" data-testid="ai-rec-use">Use Recommendation</Button>
                      <Button size="sm" variant="outline" onClick={chooseDifferentCategory} data-testid="ai-rec-different">Choose Different Category</Button>
                    </div>
                    <p className="text-xs text-slate-500 pt-1">You always have the final decision. Edit your narrative and this recommendation will refresh automatically.</p>
                  </div>
                ) : (
                  <div className="mt-3">
                    <p className="text-sm text-slate-700" data-testid="ai-rec-low-confidence">We're not confident enough to recommend a category. Please select one manually.</p>
                  </div>
                ))}
              </div>
            )}

            {(showCategoryDropdown || c.primary_category) && (
              <Field label="Primary category" hint="Pick the closest fit. You can select more below." req>
                <select data-testid="primary-category" className={inputCls} value={c.primary_category||""} onChange={e=>{ update({primary_category:e.target.value}); setShowCategoryDropdown(true); }}>
                  <option value="">Select...</option>
                  {categories.map(cat=><option key={cat.id}>{cat.name}</option>)}
                </select>
              </Field>
            )}
            <MoreDetail title="Additional context (optional)" testid="what-more">
              <Field label="What did management say or do?"><textarea rows={2} className={inputCls} value={c.mgmt_said||""} onChange={e=>update({mgmt_said:e.target.value})}/></Field>
              <Field label="What did you say or do?"><textarea rows={2} className={inputCls} value={c.employee_said||""} onChange={e=>update({employee_said:e.target.value})}/></Field>
              <Field label="Were instructions given? Did you comply?"><textarea rows={2} className={inputCls} value={c.instructions_given||""} onChange={e=>update({instructions_given:e.target.value})}/></Field>
              <Field label="Has this happened before?"><textarea rows={2} className={inputCls} value={c.prior_incidents||""} onChange={e=>update({prior_incidents:e.target.value})}/></Field>
              <Field label="Secondary categories (optional)">
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => {
                    const sel = (c.secondary_categories||[]).includes(cat.name);
                    return <button type="button" key={cat.id} onClick={()=>{
                      const cur = c.secondary_categories||[];
                      update({ secondary_categories: sel ? cur.filter(x=>x!==cat.name) : [...cur, cat.name] });
                    }} className={`text-xs px-2.5 py-1 rounded border ${sel?"bg-red-600 text-white border-red-600":"bg-white text-slate-700 border-slate-300"}`}>{cat.name}</button>;
                  })}
                </div>
              </Field>
            </MoreDetail>
          </div>
        )}

        {/* STEP 2: When */}
        {step===1 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-display">Step 2 — When</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Date of incident" req><input data-testid="incident-date" type="date" className={inputCls} value={c.incident_date||""} onChange={e=>update({incident_date:e.target.value})}/></Field>
              <Field label="Time"><input data-testid="incident-time" type="time" className={inputCls} value={c.incident_time||""} onChange={e=>update({incident_time:e.target.value})}/></Field>
              <Field label="When did you become aware?"><input type="date" className={inputCls} value={c.date_aware||""} onChange={e=>update({date_aware:e.target.value})}/></Field>
            </div>

            {deadlineHint && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="deadline-hint">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5"/>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-amber-900">Estimated filing deadline: {deadlineHint.estimated_days} {deadlineHint.unit}</div>
                    <p className="text-sm text-amber-900 mt-1">{deadlineHint.note}</p>
                    <p className="text-xs text-amber-800 mt-2 border-t border-amber-200 pt-2">{deadlineHint.disclaimer}</p>
                  </div>
                </div>
              </div>
            )}

            <MoreDetail title="Discipline / removal details (optional)" testid="when-more">
              {(inferenceApplied.discipline_issued || inferenceApplied.removed_from_service) && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm" data-testid="discipline-inference-card">
                  <div className="font-semibold text-blue-900 flex items-center gap-2"><Sparkles className="h-4 w-4"/>FMG concluded — you can correct any of these</div>
                  <ul className="mt-1.5 space-y-1 text-blue-900">
                    {inferenceApplied.discipline_issued && (
                      <li>• Discipline was issued: <b>{c.discipline_issued ? "Yes" : "No"}</b> <span className="text-blue-800 text-xs">({inferenceApplied.discipline_issued.why})</span></li>
                    )}
                    {inferenceApplied.removed_from_service && (
                      <li>• Removed from service: <b>{c.removed_from_service ? "Yes" : "No"}</b> <span className="text-blue-800 text-xs">({inferenceApplied.removed_from_service.why})</span></li>
                    )}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Was discipline issued?"><select className={inputCls} value={c.discipline_issued===true?"yes":c.discipline_issued===false?"no":""} onChange={e=>update({discipline_issued: e.target.value==="yes"?true:e.target.value==="no"?false:null})}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select></Field>
                <Field label="Type of discipline"><input className={inputCls} value={c.discipline_type||""} onChange={e=>update({discipline_type:e.target.value})}/></Field>
                <Field label="Removed from service?"><select className={inputCls} value={c.removed_from_service===true?"yes":c.removed_from_service===false?"no":""} onChange={e=>update({removed_from_service: e.target.value==="yes"?true:e.target.value==="no"?false:null})}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select></Field>
                <Field label="Urgency"><select className={inputCls} value={c.urgency||""} onChange={e=>update({urgency:e.target.value})}><option value="">—</option><option>Low</option><option>Medium</option><option>High</option><option>Critical</option></select></Field>
              </div>
            </MoreDetail>
          </div>
        )}

        {/* STEP 3: Where */}
        {step===2 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-display">Step 3 — Where</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Work location" req><input data-testid="work-location" className={inputCls} value={c.work_location||""} onChange={e=>update({work_location:e.target.value})} placeholder="Hub, route, building"/></Field>
              <Field label="Department"><input className={inputCls} value={c.department||""} onChange={e=>update({department:e.target.value})}/></Field>
            </div>
            <Field label="Where exactly did it happen?" hint="Specific area / room / route section."><textarea rows={2} className={inputCls} value={c.where_happened||""} onChange={e=>update({where_happened:e.target.value})}/></Field>

            {supplementHint && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4" data-testid="supplement-hint">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-blue-700 flex-shrink-0 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    {supplementHint.supplement && (supplementHint.confidence === "high" || supplementHint.confidence === "medium") ? (
                      <>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <div className="text-[10px] font-semibold tracking-widest text-blue-800 uppercase">Detected Agreement</div>
                            <div className="font-bold text-blue-900 text-base" data-testid="detected-agreement-name">{supplementHint.detected_agreement}</div>
                            {supplementHint.national_master && <div className="text-xs text-blue-800 mt-0.5">Under {supplementHint.national_master}</div>}
                          </div>
                          <span className={`text-xs font-bold px-2 py-1 rounded uppercase tracking-widest ${
                            supplementHint.confidence === "high" ? "bg-green-100 text-green-800 border border-green-200" :
                            supplementHint.confidence === "medium" ? "bg-amber-100 text-amber-800 border border-amber-200" :
                            "bg-slate-100 text-slate-700 border border-slate-200"
                          }`} data-testid="agreement-confidence">
                            {supplementHint.confidence} confidence
                          </span>
                        </div>
                        {supplementHint.locked_from_profile && (
                          <p className="mt-2 text-xs text-blue-800 italic">Pre-filled from your profile — you can override anytime.</p>
                        )}
                        {supplementHint.why?.length > 0 && (
                          <div className="mt-3">
                            <div className="text-[10px] font-semibold tracking-widest text-blue-800 uppercase">Why it was selected</div>
                            <ul className="mt-1 space-y-1 text-sm text-blue-900" data-testid="agreement-why">
                              {supplementHint.why.map((r,i)=>(<li key={i} className="flex items-start gap-2"><span className="text-blue-600 flex-shrink-0">•</span><span>{r}</span></li>))}
                            </ul>
                          </div>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {!showAgreementDropdown ? (
                            <Button type="button" size="sm" variant="outline" className="border-blue-300 text-blue-900 hover:bg-blue-100"
                              onClick={()=>setShowAgreementDropdown(true)} data-testid="change-agreement-btn">
                              Change agreement
                            </Button>
                          ) : (
                            <select className={inputCls + " bg-white flex-1 min-w-[220px]"} value={supplementOverride}
                              onChange={e=>confirmAgreement(e.target.value)} data-testid="supplement-override">
                              <option value="">Select agreement...</option>
                              {(supplementHint.options||[]).map(o=><option key={o}>{o}</option>)}
                            </select>
                          )}
                          {supplementOverride && supplementOverride !== supplementHint.supplement && (
                            <span className="text-xs text-blue-800">Overridden to <b>{supplementOverride}</b>.</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] font-semibold tracking-widest text-blue-800 uppercase">Top likely agreements</div>
                        <p className="text-sm text-blue-900 mt-1">We couldn't confidently identify a single agreement from your profile. Pick the one that matches your local:</p>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="agreement-suggestions">
                          {(supplementHint.top_suggestions?.length ? supplementHint.top_suggestions : (supplementHint.options||[]).slice(0,3).map(o=>({supplement:o, agreement_name:`UPS ${o} Supplemental Agreement`, score:0, reasons:[]}))).map((s,i)=>(
                            <button type="button" key={s.supplement}
                              onClick={()=>confirmAgreement(s.supplement)}
                              className={`text-left border rounded-md p-3 hover:border-blue-400 hover:bg-white transition-colors ${supplementOverride === s.supplement ? "border-blue-500 bg-white ring-1 ring-blue-500" : "border-blue-200 bg-white/50"}`}
                              data-testid={`agreement-suggestion-${i}`}>
                              <div className="text-xs font-semibold text-blue-800">Option {i+1}</div>
                              <div className="font-bold text-blue-900 text-sm leading-snug">{s.agreement_name}</div>
                              {s.reasons?.length > 0 && <div className="text-xs text-blue-700 mt-1 line-clamp-2">{s.reasons[0]}</div>}
                            </button>
                          ))}
                        </div>
                        <p className="mt-3 text-xs text-blue-800 italic">Not sure? Ask your steward or business agent — locals can cross regional lines.</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 4: Who Was Involved */}
        {step===3 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-display">Step 4 — Who Was Involved</h2>
            <Field label="Supervisor or manager involved" req><input data-testid="supervisor" className={inputCls} value={c.supervisor||""} onChange={e=>update({supervisor:e.target.value})}/></Field>
            <Field label="Others involved">
              <textarea rows={3} className={inputCls} value={c.who_involved||""} onChange={e=>update({who_involved:e.target.value})} placeholder="Names, roles, employees or managers present..."/>
              <SpeakInsteadButton value={c.who_involved||""} onChange={v=>update({who_involved:v})} testid="who-speak"/>
            </Field>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Witnesses</h3>
              {(c.witnesses||[]).map((w,i)=>(
                <div key={i} className="flex gap-2 items-start mb-2">
                  <input placeholder="Name" className={inputCls} value={w.name||""} onChange={e=>{ const ws=[...c.witnesses]; ws[i]={...ws[i],name:e.target.value}; update({witnesses:ws}); }}/>
                  <input placeholder="Job title" className={inputCls} value={w.job_title||""} onChange={e=>{ const ws=[...c.witnesses]; ws[i]={...ws[i],job_title:e.target.value}; update({witnesses:ws}); }}/>
                  <input placeholder="Observed" className={inputCls} value={w.observed||""} onChange={e=>{ const ws=[...c.witnesses]; ws[i]={...ws[i],observed:e.target.value}; update({witnesses:ws}); }}/>
                  <Button variant="outline" size="sm" onClick={()=>update({witnesses:c.witnesses.filter((_,x)=>x!==i)})}><X className="h-4 w-4"/></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={()=>update({witnesses:[...(c.witnesses||[]),{name:"",job_title:"",observed:""}]})} data-testid="add-witness">+ Add witness</Button>
            </div>
            <MoreDetail title="Steward notification (optional)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Steward notified?"><select className={inputCls} value={c.steward_notified===true?"yes":c.steward_notified===false?"no":""} onChange={e=>update({steward_notified: e.target.value==="yes"?true:e.target.value==="no"?false:null})}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select></Field>
                <Field label="Date notified"><input type="date" className={inputCls} value={c.steward_notified_date||""} onChange={e=>update({steward_notified_date:e.target.value})}/></Field>
              </div>
            </MoreDetail>
          </div>
        )}

        {/* STEP 5: Article Analysis */}
        {step===4 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-xl font-bold font-display">Step 5 — Article Analysis</h2>
                <p className="text-sm text-slate-600 mt-1">FMG will read the facts you've entered and draft your statement, supporting facts, contract provisions, and remedy in one pass. You can edit anything below.</p>
              </div>
              <div className="flex gap-2">
                {!analysisAlreadyDone && !analyzeBusy && (
                  <Button size="sm" onClick={()=>analyzeCase({forceRegen:false})} disabled={!analysisGateOk} className="bg-red-600 hover:bg-red-700 text-white" data-testid="analyze-btn">
                    <Sparkles className="h-4 w-4 mr-1"/>Analyze My Case
                  </Button>
                )}
                {(analysisAlreadyDone || analyzeError) && !analyzeBusy && (
                  <Button size="sm" onClick={()=>analyzeCase({forceRegen:true})} variant="outline" data-testid="regenerate-btn">
                    <Sparkles className="h-4 w-4 mr-1"/>Regenerate
                  </Button>
                )}
                {analyzeBusy && (
                  <span className="text-xs text-slate-600 animate-pulse" data-testid="analyze-progress">
                    {analyzeStage === "articles" && "Reading contract articles..."}
                    {analyzeStage === "drafting" && "Drafting grievance language..."}
                    {analyzeStage === "remedy" && "Suggesting remedy..."}
                    {!analyzeStage && "Preparing analysis..."}
                  </span>
                )}
              </div>
            </div>

            {!analysisGateOk && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="analyze-gate-missing">
                <div className="text-sm text-amber-900 font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4"/>Add a little more info before we analyze</div>
                <ul className="mt-2 text-sm text-amber-900 list-disc list-inside">
                  {analysisGateReasons.map((r,i)=>(<li key={i}>{r}</li>))}
                </ul>
                <p className="mt-2 text-xs text-amber-800">Go back to earlier steps to fill these in — your work is saved.</p>
              </div>
            )}

            {analyzeError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800" data-testid="analyze-error">{analyzeError}</div>
            )}

            {confidence && (
              <div className={`rounded-md p-3 border text-sm flex items-center gap-2 ${confidence==='High'?'bg-green-50 border-green-200 text-green-900':confidence==='Medium'?'bg-amber-50 border-amber-200 text-amber-900':'bg-slate-50 border-slate-200 text-slate-700'}`} data-testid="confidence-badge">
                <Check className="h-4 w-4"/>Analysis confidence: <b>{confidence}</b>
                <span className="text-xs">— based on your facts, category, and selected articles</span>
              </div>
            )}
            {warning && <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900"><AlertTriangle className="h-4 w-4 inline mr-1"/>{warning}</div>}
            {specialWarnings.map((w,i)=><div key={i} className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800"><AlertTriangle className="h-4 w-4 inline mr-1"/>{w}</div>)}

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Primary & suggested articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {suggestions.length === 0 && <p className="text-sm text-slate-500 col-span-2">Click "Suggest articles" to see recommendations based on your category.</p>}
                {suggestions.map((a,idx)=>{
                  const onDraft = isSelected(a.article_number);
                  const autoTag = onDraft && (c.selected_articles||[]).some(x=>x.article_number===a.article_number && x.auto_added_by_fmg);
                  return (
                  <div key={a.id} className={`border rounded-md p-4 ${onDraft?"border-red-500 bg-red-50":"border-slate-200 bg-white"}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-red-600 font-semibold tracking-wide">{a.article_number}</div>
                      {idx===0 && <span className="text-xs bg-red-600 text-white rounded-full px-2 py-0.5">Primary</span>}
                    </div>
                    <div className="font-bold text-slate-900 mt-1">{a.article_title}</div>
                    <div className="text-sm text-slate-600 mt-1">{a.article_summary}</div>
                    <div className="mt-2 text-xs text-slate-500 italic">Why it may apply: matches "{c.primary_category}" category</div>
                    {autoTag && (
                      <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200" data-testid={`fmg-auto-tag-${a.article_number.replace(/[.\s]/g,'')}`}>
                        <Sparkles className="h-3 w-3"/>Suggested by FMG · Auto-added
                      </div>
                    )}
                    <Button size="sm" variant={onDraft?"default":"outline"} onClick={()=>toggleArticle(a)} className={onDraft?"mt-3 bg-red-600 hover:bg-red-700 text-white":"mt-3"} data-testid={`toggle-${a.article_number.replace(/[.\s]/g,'')}`}>
                      {onDraft ? <><Check className="h-3.5 w-3.5 mr-1"/>{autoTag ? "Remove" : "Selected"}</> : "Add to grievance"}
                    </Button>
                  </div>
                  );
                })}
              </div>
            </div>

            <MoreDetail title="Search additional articles" testid="analysis-more">
              <div className="flex gap-2 mb-3">
                <input className={inputCls} placeholder="Search articles..." value={libraryQ} onChange={e=>setLibraryQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&searchLib()} data-testid="lib-search-in-builder"/>
                <Button onClick={searchLib} className="bg-slate-900 hover:bg-slate-800 text-white">Search</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {libraryArticles.map(a=>(
                  <div key={a.id} className={`border rounded-md p-4 ${isSelected(a.article_number)?"border-red-500 bg-red-50":"border-slate-200"}`}>
                    <div className="text-xs text-red-600 font-semibold">{a.article_number}</div>
                    <div className="font-bold text-slate-900 text-sm">{a.article_title}</div>
                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">{a.article_summary}</div>
                    <Button size="sm" variant="outline" onClick={()=>toggleArticle(a)} className="mt-2">{isSelected(a.article_number)?"Remove":"Add"}</Button>
                  </div>
                ))}
              </div>
            </MoreDetail>

            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold text-slate-900 mb-2">Editable grievance language</h3>
              <Field label="Statement of the grievance"><textarea data-testid="grievance-statement" rows={5} className={inputCls} value={c.grievance_statement||""} onChange={e=>update({grievance_statement:e.target.value})}/></Field>
              <div className="mt-3"><Field label="Supporting facts"><textarea rows={4} className={inputCls} value={c.supporting_facts||""} onChange={e=>update({supporting_facts:e.target.value})}/></Field></div>
              <div className="mt-3"><Field label="Contract provisions involved"><textarea rows={2} className={inputCls} value={c.contract_provisions||""} onChange={e=>update({contract_provisions:e.target.value})}/></Field></div>
              <p className="text-xs text-slate-500 mt-2">Generated text uses ONLY the facts you entered. Review carefully and edit before providing to a steward.</p>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-900">Editable remedy</h3>
                <Button size="sm" onClick={suggestRemedy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="suggest-remedy-btn">Suggest remedies</Button>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                {REMEDIES.map(r=>{
                  const sel = (c.selected_remedies||[]).includes(r);
                  return <button type="button" key={r} onClick={()=>toggleRemedy(r)} className={`text-left text-sm border rounded-md px-3 py-2 ${sel?"bg-red-50 border-red-500 text-red-900":"bg-white border-slate-200"}`} data-testid={`remedy-${r.slice(0,15)}`}>
                    {sel && <Check className="h-3.5 w-3.5 inline mr-1 text-red-600"/>}{r}
                  </button>;
                })}
              </div>
              <div className="mt-3"><Field label="Remedy statement"><textarea data-testid="remedy-statement" rows={4} className={inputCls} value={c.remedy_statement||""} onChange={e=>update({remedy_statement:e.target.value})}/></Field></div>
            </div>

            <MoreDetail title="Evidence upload" testid="evidence-section">
              <div className="flex items-center gap-2 flex-wrap">
                <select id="ev-type" className={inputCls + " max-w-xs"}>{EVIDENCE_TYPES.map(t=><option key={t}>{t}</option>)}</select>
                <input id="ev-desc" placeholder="Description" className={inputCls}/>
                <label className="inline-flex items-center gap-1 bg-slate-900 text-white px-3 py-2 rounded-md cursor-pointer text-sm">
                  <Upload className="h-4 w-4"/>Upload
                  <input type="file" hidden data-testid="evidence-upload" onChange={e=>{
                    const f = e.target.files[0]; if (!f) return;
                    const type = document.getElementById("ev-type").value;
                    const desc = document.getElementById("ev-desc").value;
                    uploadEvidence(f, type, desc);
                  }}/>
                </label>
              </div>
              <div className="mt-3 space-y-2">
                {(c.evidence||[]).map((e,i)=>(
                  <div key={i} className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <div><b>{e.evidence_type}:</b> {e.filename} — <span className="text-slate-600">{e.description}</span></div>
                    <Button variant="outline" size="sm" onClick={()=>update({evidence: c.evidence.filter((_,x)=>x!==i)})}><X className="h-3.5 w-3.5"/></Button>
                  </div>
                ))}
              </div>
            </MoreDetail>
          </div>
        )}

        {/* STEP 6: Review */}
        {step===5 && (
          <div className="space-y-6" data-testid="review-screen">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-xl font-bold font-display">Step 6 — Review</h2>
              {readiness.isPrepared ? (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-green-100 text-green-800 border border-green-200" data-testid="readiness-ready-pill"><Check className="h-3.5 w-3.5"/>Ready to be prepared</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="readiness-incomplete-pill"><AlertTriangle className="h-3.5 w-3.5"/>Incomplete — {readiness.missing.length} missing</span>
              )}
            </div>

            {!readiness.isPrepared && (
              <section className="bg-amber-50 border border-amber-200 rounded-md p-5" data-testid="readiness-missing-panel">
                <h3 className="font-bold text-amber-900 flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4"/>Still needed to prepare this grievance</h3>
                <p className="text-xs text-amber-800 mb-3">Your draft is saved. Add the items below when ready — you can leave and come back anytime.</p>
                <ul className="space-y-1.5" data-testid="readiness-missing-list">
                  {readiness.missing.map((m,i)=>(
                    <li key={i} className="flex items-center justify-between gap-2 text-sm text-amber-900">
                      <span className="flex items-center gap-2"><X className="h-3.5 w-3.5 text-red-600"/>{m.label}</span>
                      <Button size="sm" variant="outline" onClick={()=>setStep(m.step)} data-testid={`readiness-goto-${m.key}`}>Fix in Step {m.step+1}</Button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="bg-slate-50 rounded-md p-5 border border-slate-200" data-testid="review-member">
              <h3 className="font-bold font-display text-slate-900 mb-3 flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-red-600"/>Member information</h3>
              <dl className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                <div><dt className="inline text-slate-500">Name:</dt> <dd className="inline">{[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Local Union:</dt> <dd className="inline">{user?.profile?.local_union || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Job classification:</dt> <dd className="inline">{c.job_classification || user?.profile?.job_classification || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Employee ID:</dt> <dd className="inline">{user?.profile?.employee_id || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Work location:</dt> <dd className="inline">{c.work_location || user?.profile?.work_location || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Department:</dt> <dd className="inline">{c.department || user?.profile?.department || "—"}</dd></div>
              </dl>
            </section>

            <section className="bg-slate-50 rounded-md p-5 border border-slate-200">
              <h3 className="font-bold font-display text-slate-900 mb-3 flex items-center gap-2"><ScrollText className="h-4 w-4 text-red-600"/>Incident</h3>
              <dl className="text-sm space-y-1">
                <div><dt className="inline text-slate-500">Title:</dt> <dd className="inline">{c.title || "—"}</dd></div>
                <div><dt className="inline text-slate-500">What happened:</dt><dd className="whitespace-pre-wrap mt-1">{c.what_happened || "—"}</dd></div>
                <div><dt className="inline text-slate-500">When:</dt> <dd className="inline">{c.incident_date || "—"}{c.incident_time ? ` at ${c.incident_time}` : ""}</dd></div>
                <div><dt className="inline text-slate-500">Where:</dt> <dd className="inline">{[c.work_location, c.where_happened].filter(Boolean).join(" — ") || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Category:</dt> <dd className="inline">{c.primary_category || "—"}</dd></div>
                {(c.detected_supplement || supplementHint?.detected_agreement) && (
                  <div><dt className="inline text-slate-500">Applicable agreement:</dt> <dd className="inline">{c.detected_supplement || supplementHint?.detected_agreement}</dd></div>
                )}
              </dl>
            </section>

            <section className="bg-slate-50 rounded-md p-5 border border-slate-200">
              <h3 className="font-bold font-display text-slate-900 mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-red-600"/>People involved</h3>
              <dl className="text-sm space-y-1">
                <div><dt className="inline text-slate-500">Supervisor / manager:</dt> <dd className="inline">{c.supervisor || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Others:</dt><dd className="whitespace-pre-wrap mt-1">{c.who_involved || "—"}</dd></div>
                <div><dt className="inline text-slate-500">Witnesses:</dt> <dd className="inline">{(c.witnesses||[]).length}</dd></div>
                {(c.witnesses||[]).length > 0 && (
                  <ul className="list-disc list-inside mt-1 text-xs text-slate-700">
                    {c.witnesses.map((w,i)=><li key={i}>{[w.name, w.job_title, w.observed].filter(Boolean).join(" — ")}</li>)}
                  </ul>
                )}
              </dl>
            </section>

            <section className="bg-slate-50 rounded-md p-5 border border-slate-200">
              <h3 className="font-bold font-display text-slate-900 mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-red-600"/>Contract analysis</h3>
              <div className="text-sm space-y-3">
                <div>
                  <div className="text-slate-500 mb-1">Applicable articles:</div>
                  <ul className="list-disc list-inside">
                    {(c.selected_articles||[]).map(a=>(
                      <li key={a.article_number}>
                        <b>{a.article_number}</b> — {a.article_title}
                        {a.auto_added_by_fmg && <span className="ml-2 text-xs text-blue-800 italic">(auto-selected by FMG)</span>}
                      </li>
                    ))}
                    {(!c.selected_articles || c.selected_articles.length===0) && <li className="text-slate-500">None selected</li>}
                  </ul>
                </div>
                <div><span className="text-slate-500">Contract provisions:</span><div className="whitespace-pre-wrap mt-1">{c.contract_provisions || "—"}</div></div>
                <div><span className="text-slate-500">Grievance statement:</span><div className="whitespace-pre-wrap mt-1">{c.grievance_statement || "—"}</div></div>
                <div><span className="text-slate-500">Supporting facts:</span><div className="whitespace-pre-wrap mt-1">{c.supporting_facts || "—"}</div></div>
              </div>
            </section>

            <section className="bg-slate-50 rounded-md p-5 border border-slate-200">
              <h3 className="font-bold font-display text-slate-900 mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-red-600"/>Requested remedy</h3>
              <p className="text-sm whitespace-pre-wrap">{c.remedy_statement || "—"}</p>
              {(c.selected_remedies||[]).length > 0 && <ul className="mt-2 text-xs text-slate-600 list-disc list-inside">{c.selected_remedies.map(r=><li key={r}>{r}</li>)}</ul>}
            </section>

            <section className="bg-slate-50 rounded-md p-5 border border-slate-200">
              <h3 className="font-bold font-display text-slate-900 mb-3">Evidence</h3>
              <div className="text-sm">
                <div className="text-slate-500 mb-1">Evidence: {(c.evidence||[]).length} item(s)</div>
                <ul className="list-disc list-inside">{(c.evidence||[]).map((e,i)=><li key={i}><b>{e.evidence_type}:</b> {e.filename} — {e.description}</li>)}</ul>
              </div>
            </section>

            {deadlineHint && (
              <section className="bg-amber-50 rounded-md p-5 border border-amber-200" data-testid="review-deadline">
                <h3 className="font-bold font-display text-amber-900 mb-2 flex items-center gap-2"><Clock className="h-4 w-4"/>Estimated filing deadline</h3>
                <p className="text-sm text-amber-900"><b>{deadlineHint.estimated_days} {deadlineHint.unit}</b> — {deadlineHint.note}</p>
                <p className="text-xs text-amber-800 mt-2 border-t border-amber-200 pt-2">{deadlineHint.disclaimer}</p>
              </section>
            )}

            <section className="bg-slate-100 rounded-md p-5 border border-slate-300 text-xs text-slate-700" data-testid="filing-disclaimer">
              <b>Filing disclaimer:</b> FileMyGrievance.com does not file grievances with your employer. This platform helps you prepare and organize the paperwork. The grievance must be delivered to your union representative, steward, or business agent for actual filing per your contract's procedures. Nothing here is legal advice.
            </section>

            <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
              <Button onClick={downloadPdf} disabled={pdfBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="review-pdf"><Download className="h-4 w-4 mr-1"/>{pdfBusy ? "Generating PDF..." : "Generate PDF"}</Button>
              <Button onClick={()=>setStatus("Ready for Review")} disabled={!readiness.isPrepared} variant="outline" data-testid="review-ready"
                title={readiness.isPrepared ? "" : "Add the missing items above to mark this grievance as Prepared."}>
                Mark as Prepared
              </Button>
              <Button onClick={()=>setStatus("Shared With Steward")} variant="outline" data-testid="review-shared">Mark as Provided to Steward</Button>
              <Button onClick={()=>setStatus("Filed")} variant="outline" data-testid="review-filed">I confirmed it was filed</Button>
              <Button onClick={()=>attemptExit("/dashboard")} variant="outline">Save &amp; Return to Dashboard</Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" disabled={step===0} onClick={()=>setStep(step-1)} data-testid="step-prev">Back</Button>
        <div className="flex items-center gap-2 text-xs text-slate-500"><Save className="h-3.5 w-3.5"/>Auto-saved</div>
        <Button disabled={step===STEPS.length-1} onClick={()=>setStep(step+1)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="step-next">Next</Button>
      </div>

      <AlertDialog open={!!pendingExit} onOpenChange={(o)=>{ if(!o) setPendingExit(null); }}>
        <AlertDialogContent data-testid="discard-empty-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this empty grievance?</AlertDialogTitle>
            <AlertDialogDescription>
              No information has been entered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-cancel-btn">Continue Editing</AlertDialogCancel>
            <AlertDialogAction data-testid="discard-confirm-btn" onClick={confirmDiscard} className="bg-red-600 hover:bg-red-700 text-white">Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

/** Digital Steward — one-pass AI review returning summary + articles + evidence gaps + questions + remedies. */
function DigitalStewardPanel({ caseId, caseDoc, savedReview, onSaved }) {
  const [busy, setBusy] = React.useState(false);
  const [review, setReview] = React.useState(savedReview || null);
  const [applying, setApplying] = React.useState({}); // key: 'article-<num>' or 'remedy-<idx>'
  React.useEffect(() => { setReview(savedReview || null); }, [savedReview]);

  const selectedArticles = caseDoc?.selected_articles || [];
  const selectedRemedies = caseDoc?.selected_remedies || [];
  const hasArticle = (num) => selectedArticles.some(a => a.article_number === num);
  const hasRemedy = (r) => selectedRemedies.includes(r);

  const runReview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/digital-steward`);
      setReview(data);
      toast.success(data.source === "ai" ? "Digital Steward review complete" : "Basic review complete (AI unavailable)");
      onSaved && onSaved();
    } catch (e) {
      if (e.response?.status === 429) toast.error("Too many reviews right now — please wait a minute.");
      else toast.error(e.response?.data?.detail || "Review failed");
    } finally { setBusy(false); }
  };

  const addArticle = async (a) => {
    if (hasArticle(a.article_number)) return;
    const key = `article-${a.article_number}`;
    setApplying(prev => ({...prev, [key]: true}));
    try {
      const next = [...selectedArticles, {
        article_number: a.article_number,
        article_title: a.article_title,
        article_summary: a.article_summary || "",
      }];
      await api.put(`/cases/${caseId}`, { selected_articles: next });
      toast.success(`Added ${a.article_number} to your draft`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add article");
    } finally {
      setApplying(prev => ({...prev, [key]: false}));
    }
  };

  const addRemedy = async (r, idx) => {
    if (hasRemedy(r)) return;
    const key = `remedy-${idx}`;
    setApplying(prev => ({...prev, [key]: true}));
    try {
      const next = [...selectedRemedies, r];
      await api.put(`/cases/${caseId}`, { selected_remedies: next });
      toast.success("Added to your remedies");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add remedy");
    } finally {
      setApplying(prev => ({...prev, [key]: false}));
    }
  };

  const addAllArticles = async () => {
    if (!review?.suggested_articles?.length) return;
    const toAdd = review.suggested_articles
      .filter(a => !hasArticle(a.article_number))
      .map(a => ({
        article_number: a.article_number,
        article_title: a.article_title,
        article_summary: a.article_summary || "",
      }));
    if (toAdd.length === 0) return toast.info("All suggested articles are already on your draft.");
    try {
      await api.put(`/cases/${caseId}`, { selected_articles: [...selectedArticles, ...toAdd] });
      toast.success(`Added ${toAdd.length} article(s) to your draft`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addAllRemedies = async () => {
    if (!review?.recommended_remedies?.length) return;
    const toAdd = review.recommended_remedies.filter(r => !hasRemedy(r));
    if (toAdd.length === 0) return toast.info("All suggested remedies are already on your draft.");
    try {
      await api.put(`/cases/${caseId}`, { selected_remedies: [...selectedRemedies, ...toAdd] });
      toast.success(`Added ${toAdd.length} remedy line(s) to your draft`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border-2 border-red-100 rounded-md p-6" data-testid="digital-steward-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
            <Bot className="h-5 w-5 text-red-600"/>
          </div>
          <div>
            <h2 className="font-black font-display text-slate-900 text-lg">Digital Steward Review</h2>
            <p className="text-xs text-slate-600 mt-0.5">One-pass AI review: articles to cite, evidence gaps, questions, and remedy ideas.</p>
          </div>
        </div>
        <Button onClick={runReview} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="run-steward-btn">
          <Sparkles className="h-4 w-4 mr-1"/>{busy ? "Reviewing..." : (review ? "Re-run Review" : "Run Review")}
        </Button>
      </div>

      {!review && !busy && (
        <p className="text-sm text-slate-500 italic">Not run yet. Click Run Review to have an AI steward analyze this case.</p>
      )}

      {review && (
        <div className="mt-4 space-y-5" data-testid="digital-steward-results">
          {review.reviewed_at && (
            <div className="text-xs text-slate-500">
              Reviewed {new Date(review.reviewed_at).toLocaleString()} • Confidence {Math.round((review.confidence||0)*100)}% • {review.source === "ai" ? "AI" : "Fallback"}
            </div>
          )}

          {review.summary && (
            <div>
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Case Summary</h3>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed">{review.summary}</p>
            </div>
          )}

          {review.suggested_articles?.length > 0 && (
            <div data-testid="steward-articles">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Suggested Articles ({review.suggested_articles.length})</h3>
                <button type="button" onClick={addAllArticles} className="text-xs font-semibold text-red-700 hover:text-red-800 underline underline-offset-2" data-testid="add-all-articles">Add all to draft</button>
              </div>
              <ul className="mt-2 space-y-2">
                {review.suggested_articles.map((a, i) => {
                  const added = hasArticle(a.article_number);
                  const key = `article-${a.article_number}`;
                  return (
                    <li key={i} className={`border rounded-md p-3 ${added ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-slate-900">{a.article_number} — {a.article_title}</div>
                          {a.reason && <div className="mt-1 text-xs text-slate-600">{a.reason}</div>}
                        </div>
                        {added ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-white border border-green-200 rounded px-2 py-1 whitespace-nowrap" data-testid={`article-added-${a.article_number}`}>
                            <Check className="h-3.5 w-3.5"/>On draft
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={()=>addArticle(a)} disabled={!!applying[key]} className="whitespace-nowrap" data-testid={`add-article-${a.article_number}`}>
                            <Sparkles className="h-3.5 w-3.5 mr-1"/>{applying[key] ? "Adding..." : "Add to draft"}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {review.evidence_gaps?.length > 0 && (
            <div data-testid="steward-gaps">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-amber-600"/>Evidence Gaps ({review.evidence_gaps.length})
              </h3>
              <ul className="mt-2 space-y-2">
                {review.evidence_gaps.map((g, i) => (
                  <li key={i} className="border-l-4 border-amber-400 bg-amber-50 rounded-r-md p-3">
                    <div className="text-sm font-semibold text-slate-900">{g.item}</div>
                    {g.why_it_matters && <div className="mt-0.5 text-xs text-slate-700">{g.why_it_matters}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {review.follow_up_questions?.length > 0 && (
            <div data-testid="steward-questions">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Questions to Ask ({review.follow_up_questions.length})</h3>
              <ul className="mt-2 space-y-2">
                {review.follow_up_questions.map((q, i) => (
                  <li key={i} className="border border-slate-200 rounded-md p-3">
                    <div className="text-sm text-slate-900">Q: {q.question}</div>
                    {q.why && <div className="mt-1 text-xs text-slate-500 italic">{q.why}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {review.recommended_remedies?.length > 0 && (
            <div data-testid="steward-remedies">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Recommended Remedies ({review.recommended_remedies.length})</h3>
                <button type="button" onClick={addAllRemedies} className="text-xs font-semibold text-red-700 hover:text-red-800 underline underline-offset-2" data-testid="add-all-remedies">Add all to draft</button>
              </div>
              <ul className="mt-2 space-y-1.5">
                {review.recommended_remedies.map((r, i) => {
                  const added = hasRemedy(r);
                  const key = `remedy-${i}`;
                  return (
                    <li key={i} className={`flex items-start justify-between gap-3 rounded-md p-2 ${added ? "bg-green-50" : ""}`}>
                      <div className="flex gap-2 flex-1 min-w-0">
                        <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${added ? "text-green-600" : "text-slate-300"}`}/>
                        <span className="text-sm text-slate-700">{r}</span>
                      </div>
                      {added ? (
                        <span className="text-xs font-semibold text-green-700 whitespace-nowrap" data-testid={`remedy-added-${i}`}>On draft</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={()=>addRemedy(r, i)} disabled={!!applying[key]} className="whitespace-nowrap" data-testid={`add-remedy-${i}`}>
                          {applying[key] ? "Adding..." : "Add to draft"}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <p className="text-xs text-slate-500 italic border-t border-slate-200 pt-3">{review.disclaimer}</p>
        </div>
      )}
    </div>
  );
}

/** Small pill for the case progress tracker. */
function ProgressStep({ label, done, icon: Icon, isLast }) {
  return (
    <div className="flex items-center flex-1 min-w-0" data-testid={`progress-step-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${done ? "bg-[#16A34A] text-white shadow-sm" : "bg-white border-2 border-slate-300 text-slate-400"}`}>
          {done ? <Check className="h-5 w-5 stroke-[3]"/> : <Icon className="h-4 w-4"/>}
        </div>
        <span className={`mt-2 text-[11px] font-semibold text-center max-w-[95px] leading-tight ${done ? "text-[#0D183D]" : "text-slate-500"}`}>{label}</span>
      </div>
      {!isLast && <div className={`h-[3px] flex-1 mx-1 sm:mx-2 mt-[-20px] rounded-full ${done ? "bg-[#16A34A]" : "bg-slate-200"}`}></div>}
    </div>
  );
}

/** SVG donut chart for the Digital Steward confidence score. */
function ConfidenceDonut({ percent = 0, label = "CONFIDENCE" }) {
  const p = Math.max(0, Math.min(100, Math.round(percent)));
  const r = 44, C = 2 * Math.PI * r;
  const offset = C * (1 - p / 100);
  return (
    <div className="relative flex items-center justify-center" style={{width: 128, height: 128}} data-testid="steward-confidence-donut">
      <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
        <circle cx="64" cy="64" r={r} strokeWidth="12" fill="none" stroke="#F3F5F7"/>
        <circle cx="64" cy="64" r={r} strokeWidth="12" fill="none" stroke="#D71920"
                strokeDasharray={C} strokeDashoffset={offset} strokeLinecap="round"
                style={{transition: "stroke-dashoffset 500ms ease"}}/>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-black text-[#0D183D]">{p}%</div>
        <div className="text-[9px] font-bold tracking-widest text-slate-500 uppercase">{label}</div>
      </div>
    </div>
  );
}

/** Grievance Section icon card — small red circle + label + green check. */
function SectionIconCard({ icon: Icon, label, filled, testid }) {
  return (
    <div className="flex flex-col items-center text-center gap-2 p-3 bg-white border border-slate-200 rounded-lg" data-testid={testid}>
      <div className="h-12 w-12 rounded-full bg-red-50 border border-red-100 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#D71920]"/>
      </div>
      <div className="text-[11px] font-semibold text-[#0D183D] leading-tight min-h-[26px] flex items-center">{label}</div>
      <div className={`h-6 w-6 rounded-full flex items-center justify-center ${filled ? "bg-[#16A34A] text-white" : "bg-slate-100 text-slate-300"}`}>
        {filled ? <Check className="h-3.5 w-3.5 stroke-[3]"/> : <div className="h-2 w-2 rounded-full bg-slate-300"/>}
      </div>
    </div>
  );
}

/** Card indicating whether a section of the report is filled. */
function CompletionCard({ title, filled, children, testid }) {
  return (
    <div className={`bg-white border rounded-lg p-5 ${filled ? "border-slate-200" : "border-dashed border-slate-300"}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {filled ? <Check className="h-4 w-4 text-[#16A34A] stroke-[3]"/> : <div className="h-4 w-4 rounded-full border-2 border-slate-300"/>}
        <h3 className="font-bold font-display text-[#0D183D] text-sm uppercase tracking-wide">{title}</h3>
      </div>
      <div className={`text-sm whitespace-pre-wrap ${filled ? "text-slate-700" : "text-slate-400 italic"}`}>
        {filled ? children : "Not yet completed."}
      </div>
    </div>
  );
}

/** Evidence type icon lookup. */
function EvidenceIcon({ type, className = "h-4 w-4" }) {
  const t = (type || "").toLowerCase();
  if (t.includes("photo")) return <Camera className={className}/>;
  if (t.includes("audio") || t.includes("voice")) return <Mic className={className}/>;
  if (t.includes("witness")) return <Users className={className}/>;
  if (t.includes("warning") || t.includes("letter") || t.includes("discipline")) return <FileText className={className}/>;
  return <Paperclip className={className}/>;
}

/** Evidence row for the file-list-style Case Evidence card. */
function EvidenceRow({ item }) {
  const type = (item?.evidence_type || "Attachment");
  const t = type.toLowerCase();
  const isAudio = t.includes("audio") || t.includes("voice");
  const isPhoto = t.includes("photo");
  const bg = isAudio ? "bg-red-50" : isPhoto ? "bg-red-50" : "bg-red-50";
  return (
    <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`evidence-row-${(item.file_id||item.filename||"row")}`}>
      <div className={`h-11 w-11 rounded-md ${bg} border border-red-100 flex items-center justify-center flex-shrink-0`}>
        <EvidenceIcon type={type} className="h-5 w-5 text-[#D71920]"/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#0D183D] truncate">
          {item.filename || item.description || type}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {[type, item.uploaded_at?.slice(0,10)].filter(Boolean).join(" • ")}
        </div>
      </div>
    </div>
  );
}

export function CaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = React.useState(null);
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [notes, setNotes] = React.useState([]);
  const [noteText, setNoteText] = React.useState("");
  const [notePrivate, setNotePrivate] = React.useState(false);
  const [shareEmail, setShareEmail] = React.useState("");
  const [status, setStatusVal] = React.useState("");
  const [stewardInfo, setStewardInfo] = React.useState({meeting_date:"",meeting_location:"",employer_response:"",disposition:"",settlement_terms:""});
  const stewardPanelRef = React.useRef(null);

  const isSteward = user?.role === "steward" || user?.role === "admin";
  const isOwner = c && user && c.owner_id === user.id;

  const load = async () => {
    const r = await api.get(`/cases/${id}`); setC(r.data); setStatusVal(r.data.status);
    setStewardInfo({
      meeting_date: r.data.steward_info?.meeting_date || "",
      meeting_location: r.data.steward_info?.meeting_location || "",
      employer_response: r.data.steward_info?.employer_response || "",
      disposition: r.data.steward_info?.disposition || "",
      settlement_terms: r.data.steward_info?.settlement_terms || "",
    });
    const n = await api.get(`/cases/${id}/notes`); setNotes(n.data);
  };
  React.useEffect(()=>{ load(); }, [id]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/cases/${id}/notes`, { text:noteText, is_private:notePrivate });
    setNoteText(""); setNotePrivate(false); load();
  };

  const share = async () => {
    if (!shareEmail) return;
    try { const { data } = await api.post(`/cases/${id}/share`, { steward_email:shareEmail }); toast.success(`Shared. Code: ${data.code}`); setShareEmail(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail||"Share failed"); }
  };

  const changeStatus = async () => {
    if (status === "Filed") {
      if (!window.confirm("Confirm that this grievance was actually FILED with the Company. This platform does not file grievances — a steward/business agent must deliver it per your contract. Click OK only if it was truly filed.")) return;
    }
    try {
      await api.post(`/cases/${id}/status`, { status });
      toast.success("Status updated"); load();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.info(e.response?.data?.detail || "This status requires Premium. Taking you to Billing to see your options.");
        nav("/billing");
      } else {
        toast.info(e.response?.data?.detail || "Status update failed");
      }
    }
  };

  const saveStewardInfo = async () => {
    try {
      const payload = {};
      Object.keys(stewardInfo).forEach(k => { if (stewardInfo[k]) payload[k] = stewardInfo[k]; });
      await api.post(`/cases/${id}/steward-info`, payload);
      toast.success("Steward info saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const [pdfBusy, setPdfBusy] = React.useState(false);
  const downloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await api.get(`/cases/${id}/pdf`, { responseType: "blob" });
      savePdfBlob(r.data, `${c?.case_number || "FMG-Grievance"}.pdf`);
      toast.success("Grievance PDF downloaded.");
    } catch (e) {
      if (e.response?.status === 402) setShowUpgrade(true);
      else toast.error(e.response?.status === 400 && e.response?.data?.detail ? e.response.data.detail : "PDF generation failed. Please try again.");
    } finally { setPdfBusy(false); }
  };

  const [summaryBusy, setSummaryBusy] = React.useState(false);
  const downloadSummary = async () => {
    if (summaryBusy) return;
    setSummaryBusy(true);
    try {
      const r = await api.get(`/cases/${id}/summary-pdf`, { responseType: "blob" });
      savePdfBlob(r.data, `${c?.case_number || "FMG-Case"}-Summary.pdf`);
      toast.success("Case Summary PDF downloaded.");
    } catch {
      toast.error("Case Summary download failed. Please try again.");
    } finally { setSummaryBusy(false); }
  };

  const [showDelConfirm, setShowDelConfirm] = React.useState(false);
  const delCase = async () => {
    setShowDelConfirm(false);
    try { await api.delete(`/cases/${id}`); toast.success("Deleted"); nav("/dashboard"); }
    catch (e) { toast.error(e.response?.data?.detail||"Delete failed"); }
  };

  const scrollToStewardPanel = () => {
    stewardPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!c) return <AppShell><div>Loading...</div></AppShell>;

  // Derived progress signals — all read-only, computed from existing case data.
  const hasIncident = Boolean((c.what_happened || "").trim());
  const timelineCount = (c.timeline || []).length;
  const hasTimeline = timelineCount > 0;
  const hasContract = ((c.selected_articles || []).length > 0);
  const evidence = c.evidence || [];
  const hasEvidence = evidence.length > 0;
  const hasReport = Boolean((c.grievance_statement || "").trim());
  const allDone = hasIncident && hasTimeline && hasContract && hasEvidence && hasReport;
  const isReadyForReview = allDone || ["Ready for Review","Shared With Steward","Filed","Meeting Scheduled","Employer Response Received","Pending","Settled","Denied","Closed"].includes(c.status);

  const review = c.digital_steward_review;

  return (
    <AppShell>
      <PdfUpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} nav={nav} />
      {/* ============ SECTION 1 — Grievance Report Header ============ */}
      <div className="rounded-xl p-6 sm:p-8 text-white shadow-sm" style={{backgroundColor:"#0D183D"}} data-testid="report-header">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0">
            <img src="/brand/fmg-seal.png" alt="FileMyGrievance seal" className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white p-1 shadow-md flex-shrink-0" data-testid="report-header-seal"/>
            <div className="min-w-0">
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{color:"#D71920"}}>UPS Teamsters</div>
              <h1 className="mt-1 text-3xl sm:text-4xl font-black font-display leading-tight">Grievance Report</h1>
              <div className="mt-1 text-xs text-slate-300">Prepared by FileMyGrievance.com</div>
              <div className="mt-4 text-lg font-semibold">{c.title}</div>
              {c.primary_category && <div className="text-sm text-slate-300">{c.primary_category}</div>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 min-w-[280px]">
            <div>
              <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Case No.</div>
              <div className="mt-1 text-sm font-mono text-white">{c.case_number}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Status</div>
              <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 border border-white/20 rounded-full px-2 py-0.5 text-white">
                {c.status}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Updated</div>
              <div className="mt-1 text-sm text-white">{c.updated_at?.slice(0,10)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ SECTION 2 — Hero card ============ */}
      <div className={`mt-6 rounded-xl p-6 sm:p-7 flex flex-wrap items-center justify-between gap-6 shadow-sm ${isReadyForReview ? "bg-white border-2 border-green-200" : "bg-white border border-slate-200"}`} data-testid="hero-card">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div className={`h-14 w-14 rounded-lg flex items-center justify-center flex-shrink-0 ${isReadyForReview ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-100"}`}>
            <Award className={`h-7 w-7 ${isReadyForReview ? "text-[#16A34A]" : "text-[#D71920]"}`}/>
          </div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-black font-display text-[#0D183D]">Your Grievance Report {isReadyForReview ? "is Ready" : "in Progress"}</h2>
            <p className="mt-1 text-sm text-slate-600">
              {isReadyForReview
                ? "This report is complete and ready for steward review and filing."
                : "Keep adding details, contract references, and evidence to complete the report."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy} data-testid="hero-preview-btn"><ExternalLink className="h-4 w-4 mr-1"/>Preview Report</Button>
          <Button className="text-white" style={{backgroundColor:"#D71920"}} onClick={downloadPdf} disabled={pdfBusy} data-testid="hero-download-btn"><Download className="h-4 w-4 mr-1"/>{pdfBusy ? "Generating..." : "Download PDF"}</Button>
          <Button variant="outline" onClick={()=>document.getElementById("share-sidebar")?.scrollIntoView({behavior:"smooth", block:"center"})} data-testid="hero-share-btn"><Share2 className="h-4 w-4 mr-1"/>Share with Steward</Button>
        </div>
      </div>

      {/* ============ SECTION 3 — Case Progress ============ */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="case-progress">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h2 className="text-sm font-bold text-[#0D183D] uppercase tracking-widest">Case Progress</h2>
          {isReadyForReview && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-green-50 border border-green-200 text-green-800 rounded-full px-3 py-1" data-testid="ready-badge">
              <ShieldCheck className="h-3.5 w-3.5"/>Ready for Steward Review
            </span>
          )}
        </div>
        <div className="flex items-start gap-1 sm:gap-2">
          <ProgressStep label="Incident Recorded" done={hasIncident} icon={ScrollText}/>
          <ProgressStep label="Timeline Updated" done={hasTimeline} icon={Clock}/>
          <ProgressStep label="Contract Reviewed" done={hasContract} icon={Gavel}/>
          <ProgressStep label="Evidence Attached" done={hasEvidence} icon={Paperclip}/>
          <ProgressStep label="Report Prepared" done={hasReport} icon={FileCheck2} isLast/>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">

          {/* ============ SECTION 4 — Digital Steward with donut ============ */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="digital-steward-summary">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-5 w-5 text-[#D71920]"/>
                </div>
                <div>
                  <h2 className="font-black font-display text-[#0D183D] text-lg">Digital Steward</h2>
                  <p className="text-xs text-slate-600 mt-0.5">AI-assisted analysis of your case.</p>
                </div>
              </div>
              <Button variant="outline" onClick={scrollToStewardPanel} data-testid="steward-review-full-btn">Review Full Analysis</Button>
            </div>
            {review ? (
              <div className="flex flex-wrap items-center gap-6" data-testid="steward-stats">
                <ConfidenceDonut percent={(review.confidence||0)*100}/>
                <ul className="flex-1 min-w-[220px] space-y-2">
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#D71920]"/>Articles Identified</span>
                    <span className="font-bold text-[#0D183D]">{review.suggested_articles?.length || 0}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-500"/>Evidence Gaps</span>
                    <span className="font-bold text-[#0D183D]">{review.evidence_gaps?.length || 0}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#0D183D]"/>Questions Generated</span>
                    <span className="font-bold text-[#0D183D]">{review.follow_up_questions?.length || 0}</span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#16A34A]"/>Remedies Suggested</span>
                    <span className="font-bold text-[#0D183D]">{review.recommended_remedies?.length || 0}</span>
                  </li>
                </ul>
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">Digital Steward review has not been run yet. Click Review Full Analysis to start.</p>
            )}
          </div>

          {/* ============ SECTION 5 — Case Evidence (file rows) ============ */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="case-evidence">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-[#0D183D] uppercase tracking-widest">Case Evidence <span className="text-slate-400 font-normal normal-case">({evidence.length})</span></h2>
              {isOwner && <Link to={`/new-grievance/${c.id}`} className="text-xs font-semibold text-[#D71920] hover:opacity-80">+ Add Evidence</Link>}
            </div>
            {evidence.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No evidence attached yet. Open the grievance builder to upload photos, letters, or witness statements.</p>
            ) : (
              <div className="space-y-2">
                {evidence.slice(0, 6).map((it, i) => (
                  <EvidenceRow key={i} item={it}/>
                ))}
                {evidence.length > 6 && (
                  <Link to={`/new-grievance/${c.id}`} className="block text-center text-xs font-semibold text-[#D71920] pt-2">View all {evidence.length} →</Link>
                )}
              </div>
            )}
          </div>

          {/* ============ SECTION 6 — Grievance Sections (icon row) ============ */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="grievance-sections">
            <h2 className="text-sm font-bold text-[#0D183D] uppercase tracking-widest mb-4">Grievance Sections</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="grievance-sections-row">
              <SectionIconCard icon={ScrollText} label="Statement of Grievance" filled={hasReport} testid="gs-statement"/>
              <SectionIconCard icon={ClipboardList} label="Supporting Facts" filled={Boolean((c.supporting_facts||"").trim())} testid="gs-facts"/>
              <SectionIconCard icon={Gavel} label="Contract Provisions" filled={hasContract} testid="gs-articles"/>
              <SectionIconCard icon={BadgeCheck} label="Requested Remedy" filled={Boolean((c.remedy_statement||"").trim())} testid="gs-remedy"/>
              <SectionIconCard icon={Clock} label="Timeline of Events" filled={hasTimeline} testid="gs-timeline"/>
            </div>
          </div>

          {/* ============ Completion cards (detailed content) ============ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="completion-cards">
            <CompletionCard title="Statement of Grievance" filled={hasReport} testid="cc-statement">
              {c.grievance_statement}
            </CompletionCard>
            <CompletionCard title="Supporting Facts" filled={Boolean((c.supporting_facts || "").trim())} testid="cc-facts">
              {c.supporting_facts}
            </CompletionCard>
            <CompletionCard title="Contract Provisions" filled={hasContract} testid="cc-articles">
              <ul className="space-y-1">
                {(c.selected_articles || []).map((a, i) => (
                  <li key={i} className="text-sm"><span className="font-semibold">{a.article_number}</span> — {a.article_title}</li>
                ))}
              </ul>
            </CompletionCard>
            <CompletionCard title="Requested Remedy" filled={Boolean((c.remedy_statement || "").trim())} testid="cc-remedy">
              {c.remedy_statement}
            </CompletionCard>
            <CompletionCard title="Timeline of Events" filled={hasTimeline} testid="cc-timeline">
              <ol className="space-y-1.5">
                {(c.timeline || []).map(t => (
                  <li key={t.id} className="text-sm border-l-2 pl-2" style={{borderColor:"#D71920"}}>
                    <span className="text-xs text-slate-500">{t.date?.slice(0,10)}</span> — <b>{t.event_type}:</b> {t.description}
                  </li>
                ))}
              </ol>
            </CompletionCard>
          </div>

          {/* ============ Full Digital Steward panel (target of Section 4 button) ============ */}
          <div ref={stewardPanelRef}>
            <DigitalStewardPanel caseId={id} caseDoc={c} savedReview={c.digital_steward_review} onSaved={load}/>
          </div>

          {/* ============ SECTION 7 — Complete Case File ============ */}
          <div className="rounded-xl p-6 sm:p-7 flex flex-wrap items-center justify-between gap-5 text-white shadow-sm" style={{background:"linear-gradient(135deg, #D71920 0%, #b0141a 100%)"}} data-testid="complete-case-file">
            <div className="flex items-start gap-4 min-w-0 flex-1">
              <div className="h-12 w-12 rounded-lg bg-white/15 border border-white/20 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="h-6 w-6"/>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-black font-display">Complete Case File</h2>
                <p className="mt-1 text-sm text-red-50 max-w-xl">Contains the UPS Teamsters Grievance Report, Digital Steward analysis, timeline, evidence, contract references, and supporting documentation.</p>
              </div>
            </div>
            <Button className="bg-white text-[#D71920] hover:bg-slate-100" onClick={downloadSummary} disabled={summaryBusy} data-testid="open-case-file-btn">
              <FileText className="h-4 w-4 mr-1"/>Open Case File
            </Button>
          </div>

          {/* Preserved: Steward Actions (visible to stewards/admins) */}
          {isSteward && (
            <div className="bg-white border-2 border-red-100 rounded-md p-6" data-testid="steward-panel">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold font-display text-slate-900">Steward Actions</h2>
                <span className="text-xs bg-red-50 text-red-700 border border-red-100 rounded px-2 py-1">Steward View</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block"><span className="text-sm font-medium text-slate-700">Meeting date</span>
                  <input type="date" className={inputCls} value={stewardInfo.meeting_date} onChange={e=>setStewardInfo({...stewardInfo,meeting_date:e.target.value})} data-testid="sw-meeting-date"/>
                </label>
                <label className="block"><span className="text-sm font-medium text-slate-700">Meeting location</span>
                  <input className={inputCls} value={stewardInfo.meeting_location} onChange={e=>setStewardInfo({...stewardInfo,meeting_location:e.target.value})} data-testid="sw-meeting-location"/>
                </label>
                <label className="block md:col-span-2"><span className="text-sm font-medium text-slate-700">Employer response</span>
                  <textarea rows={3} className={inputCls} value={stewardInfo.employer_response} onChange={e=>setStewardInfo({...stewardInfo,employer_response:e.target.value})} data-testid="sw-employer-response"/>
                </label>
                <label className="block"><span className="text-sm font-medium text-slate-700">Disposition</span>
                  <select className={inputCls} value={stewardInfo.disposition} onChange={e=>setStewardInfo({...stewardInfo,disposition:e.target.value})} data-testid="sw-disposition">
                    <option value="">—</option>
                    <option>Granted</option><option>Denied</option><option>Settled</option><option>Withdrawn</option><option>Pending Panel</option>
                  </select>
                </label>
                <label className="block"><span className="text-sm font-medium text-slate-700">Settlement terms</span>
                  <textarea rows={2} className={inputCls} value={stewardInfo.settlement_terms} onChange={e=>setStewardInfo({...stewardInfo,settlement_terms:e.target.value})} data-testid="sw-settlement"/>
                </label>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={saveStewardInfo} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sw-save">Save Steward Info</Button>
              </div>
            </div>
          )}

          {!isSteward && c.steward_info && Object.values(c.steward_info).some(Boolean) && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h2 className="font-bold font-display text-slate-900">Steward Updates</h2>
              <dl className="mt-3 space-y-2 text-sm">
                {c.steward_info.meeting_date && <div><dt className="text-slate-500 inline">Meeting date:</dt> <dd className="inline text-slate-900">{c.steward_info.meeting_date}{c.steward_info.meeting_location ? ` at ${c.steward_info.meeting_location}` : ""}</dd></div>}
                {c.steward_info.employer_response && <div><dt className="text-slate-500 font-medium">Employer response:</dt><dd className="mt-1 whitespace-pre-wrap">{c.steward_info.employer_response}</dd></div>}
                {c.steward_info.disposition && <div><dt className="text-slate-500 inline">Disposition:</dt> <dd className="inline text-slate-900">{c.steward_info.disposition}</dd></div>}
                {c.steward_info.settlement_terms && <div><dt className="text-slate-500 font-medium">Settlement terms:</dt><dd className="mt-1 whitespace-pre-wrap">{c.steward_info.settlement_terms}</dd></div>}
              </dl>
            </div>
          )}

          {/* Preserved: Notes */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="font-bold font-display text-slate-900">Notes</h2>
            <div className="mt-4 space-y-3">
              {notes.map(n=>(
                <div key={n.id} className={`p-3 rounded-md ${n.is_private?"bg-amber-50 border border-amber-200":"bg-slate-50 border border-slate-200"}`} data-testid={`note-${n.id}`}>
                  <div className="text-sm">{n.text}</div>
                  <div className="text-xs text-slate-500 mt-1">{n.author_name} ({n.author_role}){n.is_private && " • Private"}</div>
                </div>
              ))}
              {notes.length===0 && <p className="text-sm text-slate-500">No notes yet.</p>}
            </div>
            <div className="mt-4 space-y-2">
              <textarea rows={2} placeholder="Add a note..." value={noteText} onChange={e=>setNoteText(e.target.value)} className={inputCls} data-testid="note-input"/>
              <div className="flex justify-between items-center">
                <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={notePrivate} onChange={e=>setNotePrivate(e.target.checked)} data-testid="note-private"/> Private note {isSteward && <span className="text-xs text-slate-500">(only visible to stewards and admins)</span>}</label>
                <Button onClick={addNote} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="note-add">Add</Button>
              </div>
            </div>
          </div>
        </div>

        {/* ============ Sidebar (preserved) ============ */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-bold text-slate-900">Actions</h3>
            <div className="mt-3 space-y-2">
              {isOwner && <Link to={`/new-grievance/${c.id}`}><Button variant="outline" className="w-full" data-testid="case-edit"><Save className="h-4 w-4 mr-1"/>Edit Draft</Button></Link>}
              <Button variant="outline" className="w-full" onClick={downloadPdf} disabled={pdfBusy} data-testid="case-pdf"><Download className="h-4 w-4 mr-1"/>{pdfBusy ? "Generating..." : "Grievance PDF"}</Button>
              <Button variant="outline" className="w-full" onClick={downloadSummary} disabled={summaryBusy} data-testid="case-summary-pdf"><FileText className="h-4 w-4 mr-1"/>{summaryBusy ? "Preparing…" : "Case Summary"}</Button>
              {isOwner && c.status==="Draft" && <Button variant="outline" className="w-full text-red-700" onClick={()=>setShowDelConfirm(true)} data-testid="case-delete"><Trash2 className="h-4 w-4 mr-1"/>Delete Draft</Button>}
              <ConfirmDialog open={showDelConfirm} onOpenChange={setShowDelConfirm} title="Delete this draft?" description="This permanently deletes the draft grievance and its saved details." confirmLabel="Delete Draft" onConfirm={delCase} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-bold text-slate-900">Change status</h3>
            <select value={status} onChange={e=>setStatusVal(e.target.value)} className={inputCls + " mt-2"} data-testid="case-status-select">
              {["Draft","Ready for Review","Shared With Steward","Filed","Meeting Scheduled","Employer Response Received","Pending","Settled","Denied","Withdrawn","Closed"].map(s=><option key={s}>{s}</option>)}
            </select>
            <Button onClick={changeStatus} className="mt-2 w-full bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="case-status-btn">Update</Button>
            <p className="text-xs text-slate-500 mt-2">Use <b>Ready for Review</b> or <b>Shared With Steward</b> until a steward confirms filing.</p>
          </div>

          {isOwner && (
            <div id="share-sidebar" className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-bold text-slate-900">Share with steward</h3>
              <input placeholder="steward@email.com" value={shareEmail} onChange={e=>setShareEmail(e.target.value)} className={inputCls + " mt-2"} data-testid="share-email"/>
              <Button onClick={share} className="mt-2 w-full bg-slate-900 hover:bg-slate-800 text-white" size="sm" data-testid="share-btn"><Share2 className="h-4 w-4 mr-1"/>Share</Button>
              {c.shared_with?.length > 0 && <div className="mt-3 text-xs text-slate-600 space-y-1">Shared with: {c.shared_with.map(s=>s.steward_email).join(", ")}</div>}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
